/**
 * Micasa Contact Form UI Controller
 * Handles file upload UI, drag-and-drop, previews, and visual feedback
 */

class MicasaContactFormUI {
  constructor(formElement) {
    this.form = formElement;
    this.fileZone = formElement.querySelector('[data-file-zone]');
    this.fileInput = formElement.querySelector('[data-file-input]');
    this.fileList = formElement.querySelector('[data-file-list]');
    this.fileSizeDisplay = formElement.querySelector('[data-file-size-display]');
    this.dropArea = formElement.querySelector('.file-drop-area');
    
    // Track selected files
    this.selectedFiles = [];
    
    if (this.fileZone && this.fileInput) {
      this.init();
    }
  }

  init() {
    // File input change handler
    this.fileInput.addEventListener('change', this.handleFileSelect.bind(this));
    
    // Drag and drop handlers
    this.setupDragAndDrop();
  }

  /**
   * Setup drag and drop functionality
   */
  setupDragAndDrop() {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropArea.addEventListener(eventName, this.preventDefaults, false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      this.dropArea.addEventListener(eventName, this.highlight.bind(this), false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      this.dropArea.addEventListener(eventName, this.unhighlight.bind(this), false);
    });

    this.dropArea.addEventListener('drop', this.handleDrop.bind(this), false);
  }

  preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  highlight() {
    this.dropArea.classList.add('file-drop-area--active');
  }

  unhighlight() {
    this.dropArea.classList.remove('file-drop-area--active');
  }

  /**
   * Handle file drop
   */
  handleDrop(e) {
    const dt = e.dataTransfer;
    const files = dt.files;
    
    this.handleFiles(files);
    this.updateFileInput();
  }

  /**
   * Handle file selection from input
   */
  handleFileSelect(e) {
    const files = e.target.files;
    this.handleFiles(files);
  }

  /**
   * Process selected files
   */
  handleFiles(files) {
    // Convert FileList to Array
    this.selectedFiles = Array.from(files);
    
    // Update UI
    this.renderFilePreviews();
    this.updateFileSizeDisplay();
  }

  /**
   * Render file previews
   */
  renderFilePreviews() {
    if (this.selectedFiles.length === 0) {
      this.fileList.classList.add('hidden');
      this.fileList.innerHTML = '';
      return;
    }

    this.fileList.classList.remove('hidden');
    this.fileList.innerHTML = '';

    this.selectedFiles.forEach((file, index) => {
      const preview = this.createFilePreview(file, index);
      this.fileList.appendChild(preview);
    });
  }

  /**
   * Create single file preview element
   */
  createFilePreview(file, index) {
    const preview = document.createElement('div');
    preview.className = 'file-preview-item';
    preview.dataset.index = index;

    // File icon or image preview
    const iconContainer = document.createElement('div');
    iconContainer.className = 'file-preview-icon';

    if (file.type.startsWith('image/')) {
      const img = document.createElement('img');
      img.className = 'file-preview-image';
      img.alt = file.name;
      
      // Create object URL for preview
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      
      // Clean up object URL when image loads
      img.onload = () => URL.revokeObjectURL(objectUrl);
      
      iconContainer.appendChild(img);
    } else {
      // Show PDF icon for PDFs
      iconContainer.innerHTML = `
        <svg class="file-preview-pdf-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    // File info
    const info = document.createElement('div');
    info.className = 'file-preview-info';

    const name = document.createElement('div');
    name.className = 'file-preview-name';
    name.textContent = file.name;
    name.title = file.name;

    const size = document.createElement('div');
    size.className = 'file-preview-size text-sm text-subdued';
    size.textContent = this.formatFileSize(file.size);

    info.appendChild(name);
    info.appendChild(size);

    // Remove button
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'file-preview-remove';
    removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
    removeBtn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    removeBtn.addEventListener('click', () => this.removeFile(index));

    // Assemble preview
    preview.appendChild(iconContainer);
    preview.appendChild(info);
    preview.appendChild(removeBtn);

    return preview;
  }

  /**
   * Remove file from selection
   */
  removeFile(index) {
    this.selectedFiles.splice(index, 1);
    
    // Update the file input with remaining files
    this.updateFileInput();
    
    // Re-render previews
    this.renderFilePreviews();
    this.updateFileSizeDisplay();
  }

  /**
   * Update file input with current file list
   */
  updateFileInput() {
    // Create a new DataTransfer object
    const dt = new DataTransfer();
    
    this.selectedFiles.forEach(file => {
      dt.items.add(file);
    });
    
    // Update the file input
    this.fileInput.files = dt.files;
    
    // Dispatch change event for form validation
    this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Update file size display
   */
  updateFileSizeDisplay() {
    if (this.selectedFiles.length === 0) {
      this.fileSizeDisplay.classList.add('hidden');
      return;
    }

    const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const maxSize = 15 * 1024 * 1024; // 15MB
    const percentage = (totalSize / maxSize) * 100;

    this.fileSizeDisplay.classList.remove('hidden');
    
    let sizeClass = '';
    if (percentage > 90) {
      sizeClass = 'text-error';
    } else if (percentage > 70) {
      sizeClass = 'text-warning';
    }

    this.fileSizeDisplay.innerHTML = `
      <span class="${sizeClass}">
        ${this.formatFileSize(totalSize)} / ${this.formatFileSize(maxSize)}
        ${this.selectedFiles.length > 1 ? `(${this.selectedFiles.length} files)` : ''}
      </span>
    `;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

/**
 * Auto-initialize for all micasa-contact-form elements
 */
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('form[is="micasa-contact-form"]');
  
  forms.forEach(form => {
    new MicasaContactFormUI(form);
  });
});

// Export for potential manual initialization
window.MicasaContactFormUI = MicasaContactFormUI;
